import React, { useState } from "react";
import {
  AppBar as MuiAppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  Button,
  SwipeableDrawer,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Menu as MenuIcon,
  KeyboardDoubleArrowLeftRounded,
  KeyboardDoubleArrowRightRounded,
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import { GET_MY_PROFILE } from "graphql/query";
import { useMutation, useQuery } from "@apollo/client";
import NavLink from "./NavLink";
import { ToastContainer } from "react-toastify";

import AVATAR_IMG from "assets/images/avatar_default.jpg";
import SideDrawer from "./sideDrawer";
import { LOGOUT } from "./graphql/mutation";
import Toaster from "common/Toaster";

const drawerWidth = 250;

const rightDrawerWidth = 300;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  sideDrawerButton: {},
  menuButton: {
    marginRight: theme.spacing(1),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: theme.palette.primary.dark,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "space-between",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open, matches }) => ({
    flexGrow: 1,
    marginTop: "60px",
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    ...(!matches &&
      open && {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: `${drawerWidth}px`,
      }),
    ...(matches &&
      open && {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open, matches }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "none",
  background: "#fff",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(matches &&
    open && {
      width: `100%`,
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  ...(!matches &&
    open && {
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    }),
}));

const Layout = () => {
  const classes = useStyles();
  const theme = useTheme();

  const navigate = useNavigate();
  const matches = useMediaQuery("(max-width:600px)");
  const [selected, setSelected] = useState("");

  const [open, setOpen] = useState(true);
  const [openRightDrawer, setOpenRightDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = Boolean(anchorEl);

  const { data: { me } = { me: null } } = useQuery(GET_MY_PROFILE);

  const [logoutUser] = useMutation(LOGOUT, {
    onCompleted: (res) => {
      localStorage.clear();
      window.location.href = "/";
    },
    onError: (err) => {
      Toaster({
        type: "error",
        message: err.message,
      });
    },
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleLogout = () => {
    logoutUser();
  };

  return (
    <Box>
      <AppBar position="fixed" open={open} matches={matches}>
        <Box>
          <Toolbar>
            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Box>
        <Stack
          direction="row"
          sx={{
            px: 2,
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* <Button onClick={() => navigate("/admin/imports")}>Import</Button> */}
          {/* <Notification /> */}
          <IconButton onClick={handleClick}>
            <Avatar alt="Remy Sharp" src={AVATAR_IMG} />
          </IconButton>
        </Stack>
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={openMenu}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: "12px",
              overflow: "visible",
              p: 1,
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem>
            <Stack direction="column">
              <Typography sx={{ fontWeight: 600 }}>
                {me && me.firstName} {me && me.lastName}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "rgba(0,0,0,.5)",
                }}
              >
                {me && me.email}
              </Typography>
            </Stack>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant={matches ? null : "persistent"}
        anchor="left"
        open={open}
        onClose={handleDrawerToggle}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <Box p={2}>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#F2F3F5",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Avatar alt="Remy Sharp" src={AVATAR_IMG} />
            <Stack direction="column">
              <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                {me && me.firstName} {me && me.lastName}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(0,0,0,.7)", ml: 2 }}
              >
                admin
              </Typography>
            </Stack>
          </Box>
          <NavLink selected={selected} setSelected={setSelected} />
        </Box>
      </Drawer>
      <SwipeableDrawer
        anchor="right"
        open={openRightDrawer}
        onClose={() => setOpenRightDrawer(false)}
        onOpen={() => setOpenRightDrawer(true)}
      >
        <Box sx={{ width: rightDrawerWidth, padding: "15px" }}>
          <SideDrawer />
        </Box>
      </SwipeableDrawer>
      <Box
        sx={{
          position: "absolute",
          right: openRightDrawer ? -60 : 0,
          bottom: 100,
          zIndex: 1250,
          width: 55,
          height: 35,
          background: "#baaa83",
          borderTopLeftRadius: 30,
          borderBottomLeftRadius: 30,
          boxShadow: "0px 0px 4px rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        onClick={() => setOpenRightDrawer(true)}
      >
        <Box
          sx={{
            marginLeft: "20px",
            position: "relative",
            animation: "button-pull 1.2s linear infinite",
            "@keyframes button-pull": {
              "0%": {
                opacity: 0,
              },
              "50%": {
                opacity: 1,
              },
              "100%": {
                opacity: 0,
                marginLeft: "0",
              },
            },
          }}
        >
          <KeyboardDoubleArrowLeftRounded htmlColor="#fff" />
        </Box>
      </Box>
      <Main open={open} matches={matches}>
        <Box sx={{ px: 3 }}>
          <Outlet />
        </Box>
      </Main>
      <ToastContainer />
    </Box>
  );
};

export default Layout;
